'use client'

import { useMemo } from 'react'

import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'

import { useCarouselState } from '@/core/components/optional/use-carousel-state'
import { prefersReducedMotion } from '@/core/lib/motion'
import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

type CarouselSlide = {
  id: string
  content: ReactNode
}

type CarouselProps = {
  slides: CarouselSlide[]
  className?: string
  /** PC 이동 화살표. 터치 화면은 스와이프로 이동한다. */
  showArrows?: boolean
  /** 이미지 위 오버레이 점 표시 */
  showDots?: boolean
  ariaLabel?: string
  /** 부모 높이를 채우는 갤러리 등 각 층의 스타일 */
  classNames?: {
    frame?: string
    viewport?: string
    track?: string
    slide?: string
  }
  /** 장 하나의 폭. 기본은 한 장씩 표시한다. */
  slideBasis?: string
  /** 세로 휠·트랙패드 입력으로 가로 슬라이드를 이동한다 */
  wheelGestures?: boolean
  dragFree?: boolean
}

export default function Carousel({
  slides,
  className,
  showArrows = true,
  showDots = true,
  ariaLabel = '슬라이드',
  classNames,
  slideBasis = 'flex-[0_0_100%]',
  wheelGestures = false,
  dragFree = false,
}: CarouselProps) {
  const total = slides.length
  const plugins = useMemo(
    () =>
      wheelGestures && total > 1
        ? [WheelGesturesPlugin({ forceWheelAxis: 'x' })]
        : [],
    [wheelGestures, total]
  )
  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      active: total > 0,
      align: 'start',
      dragFree,
      containScroll: 'trimSnaps',
      watchDrag: total > 1,
    },
    plugins
  )
  const { current, canScrollPrev, canScrollNext } = useCarouselState(
    emblaApi,
    true
  )
  const arrowClass =
    'absolute top-1/2 z-[3] hidden size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-control bg-background/80 transition-colors hover:bg-background focus:outline-none focus-visible:bg-foreground focus-visible:text-background disabled:cursor-not-allowed disabled:opacity-30 over10:flex'

  return (
    <section
      aria-label={ariaLabel}
      aria-roledescription="캐러셀"
      className={cn('group/carousel', className)}
    >
      <div data-carousel-frame className={cn('relative', classNames?.frame)}>
        <div
          ref={viewportRef}
          className={cn('overflow-hidden', classNames?.viewport)}
        >
          <div
            className={cn('flex touch-pan-y select-none', classNames?.track)}
          >
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                role="group"
                aria-label={`${index + 1} / ${total}`}
                aria-roledescription="슬라이드"
                className={cn('min-w-0', slideBasis, classNames?.slide)}
              >
                {slide.content}
              </div>
            ))}
          </div>
        </div>

        {showArrows && total > 1 && (canScrollPrev || canScrollNext) && (
          <>
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev(prefersReducedMotion())}
              disabled={!canScrollPrev}
              className={cn(arrowClass, 'left-2')}
              aria-label="이전"
            >
              <LuChevronLeft aria-hidden="true" className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext(prefersReducedMotion())}
              disabled={!canScrollNext}
              className={cn(arrowClass, 'right-2')}
              aria-label="다음"
            >
              <LuChevronRight aria-hidden="true" className="size-5" />
            </button>
          </>
        )}

        {showDots && total > 1 && (
          <div className="absolute inset-x-0 bottom-0 z-[3] flex justify-center">
            {slides.map((slide, index) => (
              <button
                key={`dot-${slide.id}`}
                type="button"
                onClick={() =>
                  emblaApi?.scrollTo(index, prefersReducedMotion())
                }
                aria-current={index === current ? 'true' : undefined}
                aria-label={`슬라이드 ${index + 1}`}
                className="group/dot flex h-7 cursor-pointer items-center justify-center px-[3px] focus:outline-none"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative block h-1 overflow-hidden rounded-full bg-white/35 transition-[width,background-color] duration-500 ease-out group-hover/dot:bg-white/60 group-focus-visible/dot:bg-white/70',
                    index === current ? 'w-6' : 'w-1.5'
                  )}
                >
                  {index === current && (
                    <span className="absolute inset-0 origin-left rounded-full bg-white" />
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {total > 0 && (
        <p aria-live="polite" aria-atomic="true" className="sr-only">
          슬라이드 {current + 1} / {total}
        </p>
      )}
    </section>
  )
}
